import { el } from './ui'

// The ⋯ menu at the end of a row (the Diary's, plan v3 item B; the customer page's since its redesign, Liam 9 Oct
// 2026). A <details>; the dismiss is bound ONCE on the document because screens rebuild every row on each change, so a
// per-menu listener would pile up. Moved here unchanged from src/screens/schedule.ts.

let rowMenuDismissBound = false
function bindRowMenuDismiss(): void {
  if (rowMenuDismissBound) return
  rowMenuDismissBound = true
  const closeOpenMenus = (keep: Node | null): void => {
    for (const menu of document.querySelectorAll<HTMLDetailsElement>('details.row-menu[open]')) {
      // A tap INSIDE the menu is choosing an action, not dismissing it.
      if (!keep || !menu.contains(keep)) menu.open = false
    }
  }
  document.addEventListener('click', (e) => closeOpenMenus(e.target as Node))
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeOpenMenus(null) })
  window.addEventListener('hashchange', () => closeOpenMenus(null))
}

export function rowMenu(label: string, build: (list: HTMLElement, close: () => void) => void): HTMLElement {
  const menu = el('details', { class: 'row-menu' })
  const summary = el('summary', { 'aria-label': label, title: 'More actions' }, '⋯')
  // Tapping the ⋯ must not also select the row for placing — the same stop
  // the WhatsApp link makes. The menu still opens: that is the summary's own
  // default action, not a click that bubbles.
  summary.addEventListener('click', (e) => {
    e.stopPropagation()
    // Because that stop hides the tap from the document handler, this menu
    // has to shut its siblings itself — otherwise two menus stand open at
    // once and the wrong customer is one thumb-width away.
    if (!menu.open) for (const other of document.querySelectorAll<HTMLDetailsElement>('details.row-menu[open]')) if (other !== menu) other.open = false
  })
  const list = el('div', { class: 'row-menu-list' })
  build(list, () => { menu.open = false })
  menu.append(summary, list)
  bindRowMenuDismiss()
  menu.addEventListener('toggle', () => placeTileMenu(menu as HTMLDetailsElement, summary, list))
  return menu
}

// A To place card's ⋯ sits in a row that scrolls sideways, which cut its menu off (Liam's phone, 9 Oct 2026: "when
// dots are clicked its not ideal as you cant see it"). Its menu opens over the page instead: on a phone as a sheet
// above the tab bar, on a computer under the ⋯ (above it if there is no room below), and it closes if the page or
// the row scrolls, so it is never left floating away from its card.
// The phone's Due list (round 2) is the same: its rows sit in a sheet that scrolls, inside boxes that clip.
function placeTileMenu(menu: HTMLDetailsElement, summary: HTMLElement, list: HTMLElement): void {
  const row = menu.closest<HTMLElement>('.diary-place-list') ?? menu.closest<HTMLElement>('.diary-sheet-body') ?? menu.closest<HTMLElement>('.diary-due-col-list')
  if (!row || typeof list.getBoundingClientRect !== 'function') return
  if (!menu.open) { list.removeAttribute('style'); list.classList.remove('row-menu-floating'); return }
  const gap = 12
  const nav = document.querySelector<HTMLElement>('.owner-nav')
  const floor = nav && getComputedStyle(nav).position === 'fixed' ? nav.getBoundingClientRect().top : window.innerHeight
  const ceiling = Math.max(0, document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect().bottom ?? 0)
  list.classList.add('row-menu-floating')
  if (matchMedia('(max-width: 760px)').matches) {
    Object.assign(list.style, { position: 'fixed', left: `${gap}px`, right: `${gap}px`, top: 'auto', bottom: `${window.innerHeight - floor + gap}px`, minWidth: '0', maxWidth: 'none', maxHeight: `${Math.max(200, floor - ceiling - 2 * gap)}px`, overflowY: 'auto', zIndex: '60' })
  } else {
    const at = summary.getBoundingClientRect()
    Object.assign(list.style, { position: 'fixed', right: 'auto', top: `${at.bottom + 4}px`, bottom: 'auto', zIndex: '60', overflowY: 'auto' })
    // Its right edge under the ⋯, unless that runs it off the left of the screen.
    const wide = list.getBoundingClientRect().width
    list.style.left = `${Math.max(gap, Math.min(at.right - wide, window.innerWidth - wide - gap))}px`
    const tall = list.getBoundingClientRect().height
    if (at.bottom + 4 + tall > floor - gap && at.top - 4 - tall >= ceiling + gap) list.style.top = `${at.top - 4 - tall}px`
    else list.style.maxHeight = `${Math.max(160, floor - gap - (at.bottom + 4))}px`
  }
  const shut = (): void => { if (menu.open) menu.open = false }
  window.addEventListener('scroll', shut, { once: true, passive: true })
  row.addEventListener('scroll', shut, { once: true, passive: true })
}

